import React from 'react';
import { Pencil, ListChecks, DollarSign, Printer, Trash2 } from 'lucide-react';

/**
 * As portas de um serviço, no lugar onde ele aparece.
 *
 * Cadastro, etapas e dinheiro. São três telas separadas de propósito, e não um
 * modal único com abas: cada uma é um trabalho diferente, feito em momento
 * diferente, e quem vai lançar uma OPU não quer passar por faturamento.
 *
 * Existe como componente porque são TRÊS os lugares que precisam das mesmas
 * portas — a linha do Pipeline, o cartão do Kanban e o balão do calendário — e
 * porque foi a falta disso que obrigava a abrir outra aba para cada coisa. Era
 * o motivo de existirem uma aba de Serviços e uma de Financeiro listando os
 * mesmos serviços só para carregar botões diferentes.
 *
 * A FICHA É A QUARTA, e só aparece quando existe uma. As três primeiras levam
 * a uma tela; esta produz papel — a ficha da etapa pendente, para a equipe
 * levar ao campo. Ela entra aqui, e não numa faixa nova no cartão, porque é
 * ação de serviço e este é o lugar onde se procura ação de serviço.
 *
 * `onFicha` recebe `null` de quem chama quando a etapa pendente não tem ficha
 * (protocolo, DG): botão que imprime papel em branco sem serventia é pior que
 * botão ausente.
 *
 * `compacto` troca rótulo por ícone. No cartão do Kanban e no balão do
 * calendário não há largura para três palavras, e ali o ícone com `title`
 * carrega o mesmo significado sem quebrar a linha.
 *
 * EXCLUIR FICA SEPARADO DAS TRÊS, e só onde quem chama pede. É o oposto delas:
 * as três levam a algum lugar, esta não volta. Fica à direita, depois de um vão,
 * e em vermelho só no hover — vermelho o tempo todo numa lista de vinte linhas
 * vira vinte avisos, e aviso repetido não é aviso.
 *
 * `onExcluir` é opcional de propósito: a linha da tabela pede, o cartão do Kanban
 * e o balão do calendário não. Naqueles dois o alvo é pequeno e o clique do
 * próprio cartão está a um pixel de distância — lixeira ali é acidente à espera.
 */
const AcoesDoServico = ({
  service,
  onCadastro,
  onEtapas,
  onFinanceiro,
  onFicha = null,
  onExcluir = null,
  compacto = false,
  podeFinanceiro = true,
  className = '',
}) => {
  const portas = [
    { id: 'cadastro', rotulo: 'Cadastro', Icon: Pencil, onClick: onCadastro,
      titulo: 'Editar o cadastro do serviço' },
    { id: 'etapas', rotulo: 'Etapas', Icon: ListChecks, onClick: onEtapas,
      titulo: 'Lançar e conferir as etapas' },
    ...(podeFinanceiro ? [{ id: 'financeiro', rotulo: 'Financeiro', Icon: DollarSign,
      onClick: onFinanceiro, titulo: 'Faturar, cobrar, receber e lançar custos' }] : []),
    { id: 'ficha', rotulo: 'Ficha', Icon: Printer, onClick: onFicha,
      titulo: 'Imprimir a ficha de campo desta etapa' },
  ].filter(p => p.onClick);

  if (portas.length === 0) return null;

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {portas.map(({ id, rotulo, Icon, onClick, titulo }) => (
        <button
          key={id}
          type="button"
          // O clique não pode subir: estas portas vivem dentro de linha e de
          // cartão que já têm o seu próprio clique.
          onClick={(e) => { e.stopPropagation(); onClick(service); }}
          title={titulo}
          aria-label={rotulo}
          className={`inline-flex items-center gap-1 rounded border border-slate-200 bg-white
                      text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors
                      ${compacto ? 'p-1' : 'px-2 py-1 text-xs'}`}
        >
          <Icon size={compacto ? 13 : 12} />
          {!compacto && <span>{rotulo}</span>}
        </button>
      ))}

      {onExcluir && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onExcluir(service); }}
          title="Excluir este serviço"
          aria-label="Excluir"
          className={`ml-1 inline-flex items-center rounded border border-transparent
                      text-slate-400 hover:text-rose-700 hover:border-rose-200 hover:bg-rose-50
                      transition-colors ${compacto ? 'p-1' : 'px-2 py-1'}`}
        >
          <Trash2 size={compacto ? 13 : 12} />
        </button>
      )}
    </div>
  );
};

export default AcoesDoServico;
